import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export type PersonalLoanType =
  | 'credit_card'
  | 'student_loan'
  | 'mortgage'
  | 'auto_loan'
  | 'personal_loan'
  | 'medical_debt'
  | 'other';

export type PersonalLoanStatus = 'active' | 'paid_off' | 'cancelled';

export type InterestType = 'simple' | 'compound' | 'none';

export interface PersonalLoan {
  id: string;
  user_id: string;
  name: string;
  lender: string | null;
  loan_type: PersonalLoanType;
  original_amount: string;
  current_balance: string;
  interest_rate: string | null;
  interest_type: InterestType;
  minimum_payment: string | null;
  payment_due_day: number | null;
  status: PersonalLoanStatus;
  notes: string | null;
  paid_off_at: string | null;
  last_payment_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface LoanPayment {
  id: string;
  loan_id: string;
  user_id: string;
  payment_date: string;
  amount_paid: string;
  new_balance: string;
  notes: string | null;
  created_at: string;
}

export interface CreateLoanInput {
  name: string;
  lender?: string;
  loan_type: PersonalLoanType;
  original_amount: number;
  current_balance: number;
  interest_rate?: number;
  interest_type?: InterestType;
  minimum_payment?: number;
  payment_due_day?: number;
  notes?: string;
}

export interface UpdateLoanInput {
  name?: string;
  lender?: string | null;
  loan_type?: PersonalLoanType;
  original_amount?: number;
  current_balance?: number;
  interest_rate?: number | null;
  interest_type?: InterestType;
  minimum_payment?: number | null;
  payment_due_day?: number | null;
  status?: PersonalLoanStatus;
  notes?: string | null;
}

export interface RecordPaymentInput {
  payment_date: string;
  amount_paid: number;
  new_balance: number;
  notes?: string;
}

interface UsePersonalLoansOptions {
  includeInactive?: boolean;
}

export function usePersonalLoans(options?: UsePersonalLoansOptions) {
  const { user, session, initialized } = useAuth();
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams();
  if (options?.includeInactive) queryParams.set('includeInactive', 'true');
  const queryString = queryParams.toString();
  const endpoint = `/personal/loans${queryString ? `?${queryString}` : ''}`;

  const {
    data: loans,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['personalLoans', user?.id, options?.includeInactive],
    queryFn: async (): Promise<PersonalLoan[]> => {
      if (!session?.access_token) {
        return [];
      }

      const response = await api.get<{ loans: PersonalLoan[] }>(
        endpoint,
        session.access_token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch loans');
      }

      return response.data?.loans ?? [];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled: initialized && !!user && !!session?.access_token,
  });

  const invalidateLoans = () =>
    queryClient.invalidateQueries({ queryKey: ['personalLoans'] });

  const createLoan = useMutation({
    mutationFn: async (input: CreateLoanInput) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.post<{ loan: PersonalLoan }>(
        '/personal/loans',
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to create loan');
      return response.data!.loan;
    },
    onSuccess: invalidateLoans,
  });

  const updateLoan = useMutation({
    mutationFn: async ({ id, ...input }: UpdateLoanInput & { id: string }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.patch<{ loan: PersonalLoan }>(
        `/personal/loans/${id}`,
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to update loan');
      return response.data!.loan;
    },
    onSuccess: invalidateLoans,
  });

  const deleteLoan = useMutation({
    mutationFn: async (id: string) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.delete<{ message: string }>(
        `/personal/loans/${id}`,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to delete loan');
      return response.data!;
    },
    onSuccess: invalidateLoans,
  });

  const recordPayment = useMutation({
    mutationFn: async ({ loanId, ...input }: RecordPaymentInput & { loanId: string }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.post<{ payment: LoanPayment }>(
        `/personal/loans/${loanId}/payments`,
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to record payment');
      return response.data!.payment;
    },
    onSuccess: () => {
      invalidateLoans();
      queryClient.invalidateQueries({ queryKey: ['loanPayments'] });
    },
  });

  const updatePayment = useMutation({
    mutationFn: async ({ loanId, paymentId, ...input }: {
      loanId: string; paymentId: string;
      payment_date?: string; amount_paid?: number; new_balance?: number; notes?: string | null;
    }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.patch<{ payment: LoanPayment }>(
        `/personal/loans/${loanId}/payments/${paymentId}`,
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to update payment');
      return response.data!.payment;
    },
    onSuccess: () => {
      invalidateLoans();
      queryClient.invalidateQueries({ queryKey: ['loanPayments'] });
    },
  });

  const deletePayment = useMutation({
    mutationFn: async ({ loanId, paymentId }: { loanId: string; paymentId: string }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.delete<{ message: string }>(
        `/personal/loans/${loanId}/payments/${paymentId}`,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to delete payment');
      return response.data!;
    },
    onSuccess: () => {
      invalidateLoans();
      queryClient.invalidateQueries({ queryKey: ['loanPayments'] });
    },
  });

  return {
    loans: loans ?? [],
    isLoading,
    error,
    refetch,
    createLoan,
    updateLoan,
    deleteLoan,
    recordPayment,
    updatePayment,
    deletePayment,
  };
}

export function useLoanPayments(loanId: string | null) {
  const { session, initialized } = useAuth();

  const {
    data: payments,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['loanPayments', loanId],
    queryFn: async (): Promise<LoanPayment[]> => {
      if (!session?.access_token || !loanId) {
        return [];
      }

      const response = await api.get<{ payments: LoanPayment[] }>(
        `/personal/loans/${loanId}/payments`,
        session.access_token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch payment history');
      }

      return response.data?.payments ?? [];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled: initialized && !!session?.access_token && !!loanId,
  });

  return {
    payments: payments ?? [],
    isLoading,
    error,
    refetch,
  };
}
